import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import ArrowBackIcon from '@material-ui/icons/ArrowBackOutlined';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { colors } from 'theme';
import { AppContext } from 'context';
import { Routes } from 'routes';
import { postProduction } from 'services/apiOperations';
import { DialogWidth, ProductionForm as ProductionFormType } from 'types';
import { formatProductionBeforePost } from 'utils/normalizer';
import { goBack } from 'utils/history';
import ProductionForm from 'components/Forms/ProductionForm';
import RequiredFieldsLegend from 'components/Forms/RequiredFieldsLegend';
import BottomButtonBar from 'components/Generic/BottomButtonBar';
import PageContent from 'components/Generic/PageContent';
import { notify } from 'components/Notifier';
import Dialog from 'components/Dialog';
import EditIcon from 'components/Icons/EditIcon';

type SubmissionError = {
  status: number;
  response: {
    body: {
      violations?: {
        code: string;
        message: string;
        propertyPath: string;
      }[];
    };
  };
};

const useStyles = makeStyles({
  headerTitle: {
    marginBottom: '2rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignContent: 'center',
  },
  title: {
    margin: '0',
  },
  buttonBar: {
    display: 'flex',
    justifyContent: 'center',
    '& > *': {
      margin: '0 10px',
    },
  },
});

const ProductionCreate: React.FC = () => {
  const context = useContext(AppContext);
  const history = useHistory();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDuplicateModalOpen, setDuplicateModalOpen] = useState<boolean>(false);
  const [duplicateMaterials, setDuplicateMaterials] = useState<string>('');
  const classes = useStyles();

  const onSubmit = async (production: ProductionFormType) => {
    if (isSubmitting) {
      return;
    }

    const sortingCenterId = context.getSortingCenter?.()?.['@id'] ?? null;

    if (null === sortingCenterId) {
      notify({
        message: t('productionCreate.creation.error'),
        severity: 'error',
      });
      return;
    }

    const formattedProduction = formatProductionBeforePost(production, sortingCenterId);

    setIsSubmitting(true);
    postProduction(formattedProduction)
      .then(() => {
        notify({
          message: t('productionCreate.creation.success'),
          severity: 'success',
        });
        goBack(Routes.Productions, history);
        setIsSubmitting(false);
      })
      .catch((e: SubmissionError) => {
        if (e.status === 412) {
          if (e.response.body.violations) {
            let violations = e.response.body.violations;
            const duplicates = violations.filter(violation => violation.code === 'DUPLICATE_PRODUCTIONS');

            if (duplicates.length === 1) {
              const duplicateMaterialMessages = duplicates.map(material => material.message.toLowerCase()).join('');
              setDuplicateMaterials(duplicateMaterialMessages);
            } else if (duplicates.length > 1) {
              const duplicateMaterialMessages = duplicates.map(material => '- ' + material.message).join('\n ');
              setDuplicateMaterials(duplicateMaterialMessages);
            }

            setDuplicateModalOpen(true);
            setIsSubmitting(false);
            return;
          }
        }
      });
  };

  return (
    <>
      <Dialog
        closable={false}
        renderButton={() => {}}
        isOpen={isDuplicateModalOpen}
        title={
          duplicateMaterials.includes('-')
            ? t('productionForm.create.duplicateModal.titleMultiple', { materials: duplicateMaterials })
            : t('productionForm.create.duplicateModal.title', { materials: duplicateMaterials })
        }
        width={DialogWidth.DEFAULT}
        renderContent={() => (
          <>
            <div className={classes.buttonBar}>
              <Button
                startIcon={<ArrowBackIcon />}
                variant="outlined"
                onClick={() => {
                  goBack(Routes.Productions, history);
                }}
              >
                {t('productionForm.create.duplicateModal.backToList')}
              </Button>
              <Button
                endIcon={<EditIcon />}
                variant="outlined"
                onClick={() => {
                  setDuplicateModalOpen(false);
                }}
                style={{
                  background: colors.grey900,
                  color: 'white',
                }}
              >
                {t('productionForm.create.duplicateModal.edit')}
              </Button>
            </div>
          </>
        )}
      />
      <PageContent title={t('productions.create.title')} backRoute={Routes.Productions} withButtonBar>
        <ProductionForm onSubmit={onSubmit} />
        <RequiredFieldsLegend />
      </PageContent>
      <BottomButtonBar
        disableNext={isSubmitting}
        onClickPrevious={() => goBack(Routes.Productions, history)}
        formName="production_form"
        previousStepLabel={t('productions.create.previousStepButtonLabel')}
        nextStepLabel={t('productions.create.createButtonLabel')}
      />
    </>
  );
};
export default ProductionCreate;
